import { DepartmentMember } from "services/departments.service";
import "./department-visuals.css";

/**
 * Shared visual atoms for the Departments module.
 *
 * `departmentColor` gives every department a STABLE identity color derived from
 * its id/name — no backend column needed. If a stored color is added later,
 * swap the seed for the stored value and everything downstream keeps working.
 */

function hashCode(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = (Math.imul(31, h) + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function departmentHue(seed: string): number {
  return hashCode(seed || "?") % 360;
}

/** Saturated identity color — readable with white text on top. */
export function departmentColor(seed: string): string {
  return `hsl(${departmentHue(seed)}, 58%, 46%)`;
}

/** Very light tint of the identity color — for accents / backgrounds. */
export function departmentColorSoft(seed: string): string {
  return `hsl(${departmentHue(seed)}, 65%, 96%)`;
}

export const getInitials = (name?: string): string =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

export function isMemberInactive(m: DepartmentMember): boolean {
  return m.isDisabled === true || m.isActivated === false;
}

/** A small colored initials circle for one person. */
export function MemberAvatar({
  member,
  size = 28,
  interactive = false,
}: {
  member: DepartmentMember;
  size?: number;
  interactive?: boolean;
}) {
  const inactive = isMemberInactive(member);
  const bg = inactive
    ? "#b6bcc6"
    : departmentColor(member.name || member.companyUserId);
  const title = member.position
    ? `${member.name} — ${member.position}`
    : member.name;
  return (
    <div
      title={title}
      className={`dept-avatar${inactive ? " dept-avatar--inactive" : ""}${
        interactive ? " dept-avatar-interactive" : ""
      }`}
      style={{
        width: size,
        height: size,
        background: bg,
        fontSize: Math.round(size * 0.4),
      }}
    >
      {getInitials(member.name)}
    </div>
  );
}

/** Overlapping stack of member avatars with a "+N" overflow chip. */
export function AvatarPile({
  members,
  max = 4,
  size = 28,
}: {
  members: DepartmentMember[];
  max?: number;
  size?: number;
}) {
  const list = members || [];
  if (list.length === 0) {
    return <span style={{ color: "#8a8f98" }}>—</span>;
  }
  const shown = list.slice(0, max);
  const rest = list.length - shown.length;
  return (
    <div className="dept-pile">
      {shown.map((m, i) => (
        <div
          key={m.companyUserId}
          className="dept-pile__item"
          style={{ marginLeft: i === 0 ? 0 : -8, zIndex: shown.length - i }}
        >
          <MemberAvatar member={m} size={size} />
        </div>
      ))}
      {rest > 0 ? (
        <div
          className="dept-avatar dept-avatar--more dept-pile__item"
          title={list
            .slice(max)
            .map((m) => m.name)
            .join(", ")}
          style={{
            width: size,
            height: size,
            marginLeft: -8,
            zIndex: 0,
            fontSize: Math.round(size * 0.36),
          }}
        >
          +{rest}
        </div>
      ) : null}
    </div>
  );
}
